<template>
  <div
    class="mensagem"
    :class="{
      verde: verde,
      escuro: isDark,
      'mensagem-audio': tipoAnexo === 'audio'
    }"
  >
    <template v-if="!ehAnexo">
      {{ conteudo }}
    </template>

    <template v-else>
      <div
        class="anexo-container"
        :class="tipoAnexo === 'audio' ? 'm-0' : 'mb-1 mt-50'"
      >
        <!-- IMAGEM -->
        <b-img
          v-if="tipoAnexo === 'imagem'"
          :src="urlAnexo"
          fluid
          rounded
          style="max-width: 250px; cursor: pointer;"
          @click="abrirLink(urlAnexo)"
        />

        <!-- ===== PLAYER CUSTOMIZADO DE ÁUDIO ===== -->
        <div v-else-if="tipoAnexo === 'audio'" class="custom-audio">
          <button class="play-btn" @click="togglePlay">
            <feather-icon
              :icon="isPlaying ? 'PauseIcon' : 'PlayIcon'"
              size="18"
            />
          </button>

          <div class="audio-info">
            <div class="progress-bar" @click="seek">
              <div class="progress" :style="{ width: progress + '%' }"></div>
            </div>
            <div class="time">
              {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
            </div>
          </div>

          <!-- elemento de áudio escondido -->
          <audio
            ref="audio"
            :src="urlAnexo"
            preload="metadata"
            @timeupdate="onTimeUpdate"
            @loadedmetadata="onLoadedMetadata"
            @ended="onEnded"
          ></audio>
        </div>

        <!-- VÍDEO -->
        <video
          v-else-if="tipoAnexo === 'video'"
          :src="urlAnexo"
          controls
          style="max-width: 250px; border-radius: 8px;"
        ></video>

        <!-- ARQUIVO -->
        <div
          v-else
          class="d-flex align-items-center p-50 rounded"
          style="background: rgba(0,0,0,0.1);"
        >
          <feather-icon icon="FileIcon" size="24" class="mr-1" />
          <a
            :href="urlAnexo"
            target="_blank"
            :style="verde ? 'color: white;' : 'color: inherit;'"
            style="word-break: break-all;"
          >
            {{ nomeAnexo }}
          </a>
        </div>
      </div>
    </template>

    <!-- Horário + lixeira -->
    <div
      class="d-flex justify-content-end align-items-center"
      :class="tipoAnexo === 'audio' ? 'mt-0' : 'mt-25'"
    >
      <feather-icon
        v-if="verde && ehAnexo"
        icon="Trash2Icon"
        size="14"
        class="cursor-pointer mr-50 text-white"
        @click="$emit('deletar', id)"
        title="Excluir anexo"
      />
      <span :style="verde ? 'color: white;' : ''" style="line-height: 1;">
        {{ horario }}
      </span>
    </div>
  </div>
</template>

<script>
import useAppConfig from '@core/app-config/useAppConfig'
import { computed } from '@vue/composition-api'
import { BImg } from 'bootstrap-vue'

export default {
  name: 'MensagemDaConversaAtiva',
  components: { BImg },
  props: ['id', 'conteudo', 'horario', 'verde'],

  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      progress: 0
    }
  },

  setup() {
    const { skin } = useAppConfig()
    const isDark = computed(() => skin.value === 'dark')
    return { isDark }
  },

  computed: {
    ehAnexo() {
      return this.conteudo && this.conteudo.startsWith('[ANEXO]')
    },
    dadosAnexo() {
      if (!this.ehAnexo) return null
      const partes = this.conteudo.replace('[ANEXO]', '').split('|')
      const hashS3 = partes[0]
      const nomeOriginal = partes[1] || hashS3
      const extensao = hashS3.split('.').pop().toLowerCase()
      return { hashS3, nomeOriginal, extensao }
    },
    urlAnexo() {
      if (!this.dadosAnexo) return ''
      const baseS3 = 'https://smartprotese-v2.s3.sa-east-1.amazonaws.com/smartprotese/'
      return baseS3 + this.dadosAnexo.hashS3
    },
    nomeAnexo() {
      return this.dadosAnexo ? this.dadosAnexo.nomeOriginal : ''
    },
    tipoAnexo() {
      if (!this.dadosAnexo) return 'texto'
      const ext = this.dadosAnexo.extensao

      if (['mp3', 'wav', 'ogg', 'oga', 'mpeg', 'm4a', 'aac', 'webm', 'opus'].includes(ext)) {
        return 'audio'
      }
      if (['mp4', 'mov', 'avi', 'mkv', 'mpg', 'mpeg4', 'wmv', 'flv'].includes(ext)) {
        return 'video'
      }
      if (['jpg', 'jpeg', 'png', 'bmp', 'gif', 'webp', 'svg'].includes(ext)) {
        return 'imagem'
      }
      return 'arquivo'
    }
  },

  methods: {
    abrirLink(url) {
      window.open(url, '_blank')
    },

    togglePlay() {
      const audio = this.$refs.audio
      if (!audio) return

      if (this.isPlaying) {
        audio.pause()
        this.isPlaying = false
      } else {
        // Pausa outros áudios da conversa (opcional, mas recomendado)
        document.querySelectorAll('audio').forEach(a => {
          if (a !== audio) a.pause()
        })
        audio.play()
        this.isPlaying = true
      }
    },

    onTimeUpdate() {
      const audio = this.$refs.audio
      if (!audio) return
      this.currentTime = audio.currentTime
      this.progress = (audio.currentTime / audio.duration) * 100 || 0
    },

    onLoadedMetadata() {
      const audio = this.$refs.audio
      if (audio) this.duration = audio.duration
    },

    onEnded() {
      this.isPlaying = false
      this.currentTime = 0
      this.progress = 0
    },

    seek(e) {
      const audio = this.$refs.audio
      if (!audio || !this.duration) return

      const rect = e.currentTarget.getBoundingClientRect()
      const percent = (e.clientX - rect.left) / rect.width
      audio.currentTime = percent * this.duration
    },

    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '0:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style>
.mensagem {
  font-size: 1rem;
  box-shadow: 0 4px 8px 0 rgb(34 41 47 / 12%);
  background: white;
  color: #000000;
  margin: 10px 60px;
  padding: 10px 14px;
  border-radius: 5px;
  width: fit-content;
  display: flex;
  flex-direction: column;
}

.mensagem.escuro {
  background: #3a3a3a;
  color: #ffffff;
}

.mensagem.escuro span {
  color: #bbbbbb;
}

.mensagem.verde {
  background: linear-gradient(118deg, #0679fd, rgba(6, 121, 253, 0.7));
  color: white;
  align-self: flex-end;
}

.mensagem span {
  font-size: 11px;
  color: gray;
}

/* ===== ÁUDIO CUSTOMIZADO ===== */
.mensagem.mensagem-audio {
  padding: 8px 10px 6px 10px !important;
}

.mensagem.mensagem-audio .anexo-container {
  margin: 0 !important;
}

.custom-audio {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 260px;
  min-height: 42px;
}

.play-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.25);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s;
}

.play-btn:hover {
  background: rgba(255, 255, 255, 0.4);
}

.mensagem:not(.verde) .play-btn {
  background: rgba(0, 0, 0, 0.08);
  color: #333;
}

.mensagem:not(.verde) .play-btn:hover {
  background: rgba(0, 0, 0, 0.15);
}

.audio-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.progress-bar {
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
}

.mensagem:not(.verde) .progress-bar {
  background: rgba(0, 0, 0, 0.15);
}

.progress {
  height: 100%;
  background: white;
  border-radius: 4px;
  width: 0%;
  transition: width 0.1s linear;
}

.mensagem:not(.verde) .progress {
  background: #0679fd;
}

.time {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1;
}

.mensagem:not(.verde) .time {
  color: #666;
}
</style>